import React, { useCallback, useEffect, useState } from "react";
import { variantGroupsAPI } from "../services/api";
import { useI18n } from "../i18n";
import useShopCurrency from "../hooks/useShopCurrency";
import { formatMoney } from "../pos/utils/money";
import { productImageUrl } from "../pos/utils/images";
import RangeForm from "../components/RangeForm";
import RangeDetail from "../components/RangeDetail";
import { useMembership } from "../context/MembershipContext";

/**
 * Ranges: a model or brand line sold in several flavours or colours — the
 * Vozol Gear 12000, a salt juice brand. The variants themselves are ordinary
 * products; a range is what lets the register keep them folded into one tile
 * and lets the owner add twenty of them from a pasted list.
 */
const Ranges = () => {
  const { t } = useI18n();
  const { can } = useMembership();
  const currency = useShopCurrency();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await variantGroupsAPI.list();
      setGroups(data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.message || t("ranges.loadFailed"));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
            {t("ranges.title")}
          </h1>
          <p className="text-gray-600 mt-1 max-w-2xl">{t("ranges.subtitle")}</p>
        </div>
        {can("products.edit") && (
          <button
            onClick={() => setCreating(true)}
            className="bg-primary-600 hover:bg-primary-700 text-white px-5 py-3 rounded-lg font-semibold shadow-lg transition-all"
          >
            <span className="text-xl me-2 font-bold">+</span>
            {t("ranges.new")}
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {groups.length === 0 && !error ? (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center">
          <div className="text-6xl mb-4">🗂️</div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">
            {t("ranges.emptyTitle")}
          </h3>
          <p className="text-gray-600">{t("ranges.emptyHint")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {groups.map((group) => {
            const image = productImageUrl(group.images?.[0] || group.image_url);

            return (
              <button
                key={group.id}
                type="button"
                onClick={() => setOpenId(group.id)}
                className="text-start bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all"
              >
                <div className="h-40 bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center overflow-hidden">
                  {image ? (
                    <img src={image} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-6xl">🗂️</span>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-lg text-gray-800 truncate">
                    {group.name}
                  </h3>
                  <p className="text-sm text-gray-600 truncate">
                    {[group.brand, group.category].filter(Boolean).join(" · ")}
                  </p>
                  <div className="flex items-center justify-between gap-2 mt-3">
                    <span className="text-lg font-bold text-primary-600">
                      {formatMoney(group.price, currency)}
                    </span>
                    <span className="text-sm text-gray-600 text-end">
                      {t("ranges.card.variants", { count: group.member_count })}
                      {group.member_count > 0 && (
                        <>
                          {" · "}
                          {t("ranges.card.inStock", { count: group.total_stock })}
                        </>
                      )}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {creating && (
        <RangeForm
          onClose={() => setCreating(false)}
          onSaved={(group) => {
            setCreating(false);
            load();
            // Straight on to adding its variants — that is what a new range is for
            setOpenId(group.id);
          }}
        />
      )}

      {openId && (
        <RangeDetail
          groupId={openId}
          onClose={() => setOpenId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
};

export default Ranges;
